import { isNaN } from "lodash"
import { IntlShape } from "react-intl"
import { DateFormats } from "~/enums/languages"

type ValidationFunction<T> = (value: unknown, intl: IntlShape) => T
type Locales = Exclude<DateFormats, DateFormats.SYSTEM>

const LOCALE_PATTERN: Record<Locales, RegExp> = {
  [DateFormats.DE]: /^[-+]?\d+(\.\d{3})*(,\d+)?$/,
  [DateFormats.EN_US]: /^[-+]?\d+(,\d{3})*(\.\d+)?$/,
  [DateFormats.EN_GB]: /^[-+]?\d+(,\d{3})*(\.\d+)?$/,
  [DateFormats.ES]: /^[-+]?\d+(\.\d{3})*(,\d+)?$/,
  [DateFormats.FR]: /^-?\d{1,3}(?:[\u00A0\u202F]?\d{3})*(?:,\d+)?$/,
  [DateFormats.HU]: /^[-+]?\d+(?:[.\u00A0\u202F]\d{3})*(?:,\d+)?$/,
  [DateFormats.PT]: /^[-+]?\d+(\.\d{3})*(,\d+)?$/,
  [DateFormats.CS_CZ]: /^-?\d{1,3}(?:[\u00A0\u202F]?\d{3})*(?:,\d+)?$/,
  [DateFormats.SK]: /^-?\d{1,3}(?:[\u00A0\u202F]?\d{3})*(?:,\d+)?$/,
  [DateFormats.SL]: /^[-+]?\d*(\.\d{3})*(,?\d*)?$/,
  [DateFormats.IT]: /^[-+]?\d+(\.\d{3})*(,\d+)?$/,
  [DateFormats.PL]: /^-?\d{1,3}(?:[\u00A0\u202F]?\d{3})*(?:,\d+)?$/,
}

const LOCALE_FORMAT: Record<Locales, (value: string) => number> = {
  [DateFormats.DE]: value => Number(value.replace(/\./g, "").replace(",", ".")),
  [DateFormats.EN_US]: value => Number(value.replace(/,/g, "")),
  [DateFormats.EN_GB]: value => Number(value.replace(/,/g, "")),
  [DateFormats.ES]: value => Number(value.replace(/\./g, "").replace(",", ".")),
  [DateFormats.FR]: value => Number(value.replace(/[\u00A0\u202F]/g, "").replace(",", ".")),
  [DateFormats.HU]: value => Number(value.replace(/[.\u00A0\u202F]/g, "").replace(",", ".")),
  [DateFormats.PT]: value => Number(value.replace(/\./g, "").replace(",", ".")),
  [DateFormats.CS_CZ]: value => Number(value.replace(/[\u00A0\u202F]/g, "").replace(",", ".")),
  [DateFormats.SK]: value => Number(value.replace(/[\u00A0\u202F]/g, "").replace(",", ".")),
  [DateFormats.SL]: value => Number(value.replace(/\./g, "").replace(",", ".")),
  [DateFormats.IT]: value => Number(value.replace(/\./g, "").replace(",", ".")),
  [DateFormats.PL]: value => Number(value.replace(/[\u00A0\u202F]/g, "").replace(",", ".")),
}

export const isNumeric: ValidationFunction<boolean> = value =>
  value !== null && value !== undefined && value.toString().length > 0 && !Number.isNaN(Number(value))

export const isDecimal: ValidationFunction<boolean> = value => !value || typeof value === "number"

export const isLocalizedDecimal: ValidationFunction<boolean> = (value, intl) => {
  const locale = intl.locale as Locales
  const pattern = LOCALE_PATTERN[locale]
  return !value || (typeof value === "string" && pattern !== undefined && pattern.test(value))
}

export const parseDecimal: ValidationFunction<string | number> = (value, intl) => {
  if (!value && value !== 0) return null
  if (typeof value === "number") return value
  if (typeof value !== "string") return null
  // Only parse if fully matches locale pattern
  const pattern = LOCALE_PATTERN[intl.locale as keyof typeof LOCALE_PATTERN]
  if (!pattern.test(value)) return null
  // Remove leading/trailing whitespace
  const trimmed = value.trim()
  // Parse using locale format
  const parsed = LOCALE_FORMAT[intl.locale as keyof typeof LOCALE_FORMAT](trimmed)
  // If result is NaN, return null
  if (isNaN(parsed)) return null
  return parsed
}

export const formatDecimal: ValidationFunction<string | number> = (value, intl) => {
  if (!value && value !== 0) return ""
  if (typeof value !== "number") throw new Error("Expected decimal")
  return intl.formatNumber(value, { maximumFractionDigits: 2 })
}

export const roundHalfUp = (num: number, decimals = 2) => {
  if (num === null || num === undefined) return null
  const shifter = 10 ** decimals
  return Math.round((num + Number.EPSILON) * shifter) / shifter
}

export const normalizeDecimal = (value: string, separator: string) => {
  const parts = value.split(separator)
  let filteredValue = parts[0].replace(/[^0-9]/g, "")

  if (parts.length > 1) {
    filteredValue +=
      separator +
      parts
        .slice(1)
        .join("")
        .replace(/[^0-9]/g, "")
  }

  if (filteredValue.length > 1 && filteredValue.startsWith("0") && !filteredValue.startsWith(`0${separator}`)) {
    filteredValue = filteredValue.replace(/^0+/, "")
    if (filteredValue.startsWith(separator)) {
      filteredValue = `0${filteredValue}`
    }
    if (filteredValue === "") {
      filteredValue = "0"
    }
  }
  return filteredValue
}

export const parseDecimalWithSeparator = (value: string, intl: IntlShape) => {
  if (typeof value !== "string" || value === "") return null

  const separator = intl.formatNumber(0.1).substring(1, 2)
  const normalizedValue = value.replace(separator, ".").replace(/[ \u00A0\u202F]/g, "")

  if (normalizedValue === "." || isNaN(parseFloat(normalizedValue)) || !Number.isFinite(parseFloat(normalizedValue))) {
    return null
  }

  return parseFloat(normalizedValue)
}
